import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {minZero} from '@shared/helpers/lang'

import {
    PayMethod,
    PayMethodType,
    NormalTabMethodType,
    NormalTabMethod,
    MaximumUsingChargePointAmount,
    GeneralMobileCodeType,
    PayMethodVoucherType,
    NormalPaymentStatus,
    PaymentMethodStatusType,
    RechargePaymentStatusType,
    SelectableRechargePaymentStatus,
} from '@web/core/constants'
import {
    paymentAmountInfoSelectors,
    paymentMethodInfoSelector,
    rechargeablePointAmountSelector,
    rechargePaymentInfoSelector,
} from '@web/selectors/paymentMethodInfo'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {mobileEasyPayInfoSelector} from '@web/slices/mobile/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {paymentSelector} from '@web/slices/paymentMethod/slice'

import {RootState, SelectPayMethodActionPayload, ThunkApi} from './interfaces'

function getRechargePointPayload(state: RootState, rechargePointAmount: number): SelectPayMethodActionPayload {
    const {paymentAmount} = paymentAmountInfoSelectors(state)
    const {complexEnabled} = paymentMethodInfoSelector(state)
    const {complex} = rechargePaymentInfoSelector(state)
    const {amount: chargePointAmount} = chargePointSelector(state)
    const {isUsedLikePayMoney} = payMoneyInfoSelector(state)
    const {
        selected: {payMethods, prevPayMethod},
        complex: {selectionType},
    } = paymentSelector(state)

    const usingPayMethodAmount = minZero(
        paymentAmount -
            Math.min(rechargePointAmount + (isUsedLikePayMoney ? chargePointAmount : 0), MaximumUsingChargePointAmount),
    )

    if (usingPayMethodAmount > 0) {
        /**
         * 현재 상태에서 복합 결제 추가수단을 선택해주지 않아도 되는 케이스. 이미 충전결제 추가결제수단이 결정되어있거나,
         * 복합결제수단 선택방법이 순차적인 서비스.
         */
        const complexPaymentMethodSelectedAlready = payMethods.length > 1
        const onlySinglePaymentMethodSelectable = selectionType === 'SEQUENTIAL'

        if ((complexEnabled && complexPaymentMethodSelectedAlready) || onlySinglePaymentMethodSelectable) {
            return {
                selectedPayMethod: null,
                payMethods,
                rechargingPointAmount: rechargePointAmount,
            }
        }
        if (complex.available) {
            return {
                selectedPayMethod: prevPayMethod,
                payMethods: [PayMethod.RECHARGE_POINT, prevPayMethod],
                rechargingPointAmount: rechargePointAmount,
            }
        }
    }
    return {
        selectedPayMethod: null,
        payMethods: [PayMethod.RECHARGE_POINT],
        rechargingPointAmount: rechargePointAmount,
    }
}

/** 충전금 변경 action */
export const changeRechargePoint = createAsyncThunk<SelectPayMethodActionPayload, number, ThunkApi>(
    'paymentMethod/changeRechargePoint',
    (rechargePointAmount, {getState}) => {
        const state = getState()
        return getRechargePointPayload(state, rechargePointAmount)
    },
)

/** 최대 충전금액 으로 적용 action */
export const setMaximumRechargePoint = createAsyncThunk<SelectPayMethodActionPayload, void, ThunkApi>(
    'paymentMethod/setMaximumRechargePoint',
    (_, {getState, rejectWithValue}) => {
        const state = getState()
        const {selectedRechargePayMethod} = paymentMethodInfoSelector(state)

        if (!selectedRechargePayMethod) {
            return rejectWithValue('NEED_RECHARGE_ENABLED')
        }

        return getRechargePointPayload(state, rechargeablePointAmountSelector(state))
    },
)

/** 복합결제 선택 action */
export const selectComplexPayMethod = createAsyncThunk<SelectPayMethodActionPayload, void, ThunkApi>(
    'paymentMethod/selectComplexPayMethod',
    async (_, {getState}) => {
        const state = getState()
        const {
            defaultPayMethod,
            selected: {prevPayMethod},
        } = paymentSelector(state)

        const selectedPayMethod = prevPayMethod ?? defaultPayMethod

        return {
            selectedPayMethod,
            payMethods: [PayMethod.RECHARGE_POINT, selectedPayMethod],
            rechargingPointAmount: rechargeablePointAmountSelector(state),
        }
    },
)

/**
 * 충전결제수단 선택 action
 * - selectPayMethodTab 와 통합 필요
 **/
export const selectRechargePayMethod = createAsyncThunk<
    SelectPayMethodActionPayload,
    void,
    ThunkApi & {rejectValue: PaymentMethodStatusType | RechargePaymentStatusType}
>('paymentMethod/selectRechargePayMethod', (_, {getState, rejectWithValue}) => {
    const state = getState()
    const {status} = paymentMethodInfoSelector(state)

    const {status: rechargeStatus} = rechargePaymentInfoSelector(state)
    if (status !== 'NORMAL') {
        return rejectWithValue(status)
    }
    if (!SelectableRechargePaymentStatus.includes(rechargeStatus)) {
        return rejectWithValue(rechargeStatus)
    }

    return getRechargePointPayload(state, rechargeablePointAmountSelector(state))
})

/**
 * @deprecated
 * 일반 결제 선택
 * - 일반결제 > 신용카드 Tab 선택
 * - 일반결제 > 휴대폰 Tab 선택
 * - 일반결제 > 나중에 결제 Tab 선택
 **/
export const selectNormalPayMethodTab = createAsyncThunk<
    {
        payMethods: PayMethodType[]
        selectedPayMethod: PayMethodType
    },
    NormalTabMethodType,
    ThunkApi
>('paymentMethod/selectNormalPayMethodTab', (selectedNormalPayMethod, {getState, rejectWithValue}) => {
    const state = getState()
    const {
        selected: {payMethods: currentPayMethods},
    } = paymentSelector(state)
    const {enabled: payLaterUsed} = payLaterSelector(state)
    const {complexEnabled} = paymentMethodInfoSelector(state)

    if (selectedNormalPayMethod === NormalTabMethod.VIRTUAL_ACCOUNT && payLaterUsed) {
        return rejectWithValue(NormalPaymentStatus.PAY_LATER_IMPOSSIBLE_USE_VIRTUAL_ACCOUNT)
    }

    const nextPayMethod = (function () {
        if (selectedNormalPayMethod === NormalTabMethod.CARD) {
            return PayMethod.CARD
        } else if (selectedNormalPayMethod === NormalTabMethod.MOBILE) {
            // 일반결제 휴대폰 Tab 이 선택되었을때, 간편결제 등록 상태이면, 간편결제 선택
            const {isRegistration} = mobileEasyPayInfoSelector(state)
            return isRegistration ? PayMethod.MOBILE_EASY_PAY : PayMethod.MOBILE
        } else if (selectedNormalPayMethod === NormalTabMethod.VIRTUAL_ACCOUNT) {
            // 일반결제 나중에 결제 Tab 이 선택되었을때, 이전에 무통장 입금을 선택하였다면 나중에 무통장 입금 선택
            return PayMethod.VIRTUAL_ACCOUNT
        } else if (selectedNormalPayMethod === NormalTabMethod.WELFARE_CARD) {
            return PayMethod.WELFARE_CARD
        } else if (selectedNormalPayMethod === NormalTabMethod.VOUCHER) {
            return PayMethod.CULTURE_LAND
        } else if (selectedNormalPayMethod === NormalTabMethod.MONEY_TOKEN) {
            return PayMethod.MONEY_TOKEN
        } else if (selectedNormalPayMethod === NormalTabMethod.PAY_PAL) {
            return PayMethod.PAY_PAL
        }
        return null
    })()

    if (!nextPayMethod || currentPayMethods.includes(nextPayMethod)) {
        return rejectWithValue('NONE')
    }

    return {
        payMethods: complexEnabled ? [PayMethod.RECHARGE_POINT, nextPayMethod] : [nextPayMethod],
        selectedPayMethod: nextPayMethod,
    }
})

export {
    selectEasyAccountPayMethod,
    selectCardPayMethod,
    selectCardEasyPayPayMethod,
    selectRechargePointPayMethod,
    selectMobilePayMethodNext,
    selectMobileEasyPayPayMethod,
    selectWelfareCardPayMethod,
    selectCultureLandPayMethod,
    selectHappyMoneyPayMethod,
    selectMoneyTokenPayMethod,
    selectPayPalPayMethod,
    selectVirtualAccountPayMethod,
    selectSamsungPayPayMethod,
    selectApplePayPayMethod,
    selectFamilyPayPayMethod,
    unselectPayMethod,
    updatePaymentMethod,
    selectSinglePaymentMethod,
    SelectPaymentMethodActions,
    isAnyOfSelectPaymentMethodActions,
    getSelectedPayMethodAction,
} from './selected'

export type {PaymentMethodActionType} from './selected'

/** 모바일 결제 구분 선택 */
export const selectMobilePayMethod = createAction<GeneralMobileCodeType>('paymentMethod/selectMobilePayMethod')

/** 상품권 결제구분 선택 */
export const selectVoucherMethod = createAction<PayMethodVoucherType>('paymentMethod/selectVoucherMethod')
